import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _get from "@babel/runtime/helpers/get";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function () { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }
import { featureFilter } from '@mapbox/mapbox-gl-style-spec';
import Style from "../Core/Style";
import TMSSource from "./TMSSource";
import Fetcher from "../Provider/Fetcher";
import urlParser from "../Parser/MapBoxUrlParser";
function toTMSUrl(url) {
  return url.replace(/\{/g, '${');
}

/**
 * @classdesc
 * VectorTilesSource are object containing informations on how to fetch vector
 * tiles resources.
 *
 * @property {function} filter - function to filter vector tiles layers, the
 * parameter function is a layer.
 * @property {boolean} [symbolToCircle=false] - If true, all symbols from a tile
 * will be considered as circle, and render as circles.
 */
var VectorTilesSource = /*#__PURE__*/function (_TMSSource) {
  _inherits(VectorTilesSource, _TMSSource);
  var _super = _createSuper(VectorTilesSource);
  /**
   * @param {Object} source - An object that can contain all properties of a
   * VectorTilesSource and {@link Source}.
   * @param {string|Object} source.style - The URL of the JSON style, of the
   * JSON style directly.
   * @param {string} [source.sprite] - The base URL to load informations about
   * the sprite of the style. If this is set, it overrides the `sprite` value
   * of the `source.style`. A style's sprite property supplies a URL template
   * for loading small images.
   * ```js
   * {
   *      sprite: 'http//:xxxxx/maps/sprites/'
   * }
   * ```
   * A valid sprite source must supply two types of files:
   * * An index file, which is a JSON document containing a description of each image contained in the sprite.
   * * Image files, which are PNG images containing the sprite data.
   *
   * For more specification : [the Mapbox sprite Specification](https://docs.mapbox.com/mapbox-gl-js/style-spec/sprite/)
   *
   * @param {string} [source.url] - The base URL to load the tiles. If no url
   * is specified, it reads it from the loaded style. Read [the Mapbox Style
   * Specification](https://docs.mapbox.com/mapbox-gl-js/style-spec/sources/)
   * for more informations.
   * @param {string} [source.accessToken] - Mapbox access token
   * @constructor
   */
  function VectorTilesSource(source) {
    var _this;
    _classCallCheck(this, VectorTilesSource);
    source.format = 'application/x-protobuf;type=mapbox-vector';
    source.crs = 'EPSG:3857';
    source.isInverted = true;
    source.url = source.url || '.';
    _this = _super.call(this, source);
    var ffilter = source.filter || function () {
      return true;
    };
    _this.layers = {};
    _this.styles = {};
    var promise;
    _this.accessToken = source.accessToken;
    if (source.style) {
      if (typeof source.style == 'string') {
        var styleUrl = urlParser.normalizeStyleURL(source.style, _this.accessToken);
        promise = Fetcher.json(styleUrl, _this.networkOptions);
      } else {
        promise = Promise.resolve(source.style);
      }
    } else {
      throw new Error('New VectorTilesSource: style is required');
    }
    _this.whenReady = promise.then(function (style) {
      _this.jsonStyle = style;
      var baseurl = source.sprite || style.sprite;
      if (baseurl) {
        var spriteUrl = urlParser.normalizeSpriteURL(baseurl, '', '.json', _this.accessToken);
        return Fetcher.json(spriteUrl, _this.networkOptions).then(function (sprites) {
          _this.sprites = sprites;
          var imgUrl = urlParser.normalizeSpriteURL(baseurl, '', '.png', _this.accessToken);
          return Fetcher.texture(imgUrl, _this.networkOptions).then(function (texture) {
            _this.sprites.img = texture.image;
            return style;
          });
        });
      }
      return style;
    }).then(function (style) {
      var s = Object.keys(style.sources)[0];
      var os = style.sources[s];
      style.layers.forEach(function (layer, order) {
        layer.sourceUid = _this.uid;
        if (layer.type === 'background') {
          _this.backgroundLayer = layer;
        } else if (ffilter(layer)) {
          var _style = new Style().setFromVectorTileLayer(layer, _this.sprites, order, _this.symbolToCircle);
          _style.zoom.min = layer.minzoom || 0;
          _style.zoom.max = layer.maxzoom || 24;
          _this.styles[layer.id] = _style;
          if (!_this.layers[layer['source-layer']]) {
            _this.layers[layer['source-layer']] = [];
          }
          _this.layers[layer['source-layer']].push({
            id: layer.id,
            order: order,
            filterExpression: featureFilter(layer.filter),
            zoom: _style.zoom
          });
        }
      });
      if (_this.url == '.') {
        if (os.url) {
          var urlSource = urlParser.normalizeSourceURL(os.url, _this.accessToken);
          return Fetcher.json(urlSource, _this.networkOptions).then(function (tileJSON) {
            if (tileJSON.tiles[0]) {
              _this.url = toTMSUrl(tileJSON.tiles[0]);
            }
          });
        } else if (os.tiles[0]) {
          _this.url = toTMSUrl(os.tiles[0]);
        }
      }
    });
    return _this;
  }
  _createClass(VectorTilesSource, [{
    key: "onLayerAdded",
    value: function onLayerAdded(options) {
      var _this2 = this;
      _get(_getPrototypeOf(VectorTilesSource.prototype), "onLayerAdded", this).call(this, options);
      if (options.out.style) {
        if (options.out.isFeatureGeometryLayer && options.out.accurate) {
          console.warn('With VectorTilesSource and FeatureGeometryLayer, the accurate option is always false');
          options.out.accurate = false;
        }
        var keys = Object.keys(this.styles);
        keys.forEach(function (k) {
          _this2.styles[k].parent = options.out.style;
        });
      }
    }
  }]);
  return VectorTilesSource;
}(TMSSource);
export default VectorTilesSource;